import React, { Fragment, useState } from "react";
import { useLocation } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../Navbar/Navbar";
import Footer from "../footer/Footer";
import Scrollbar from "../scrollbar/scrollbar";

const ContactForm = () => {
  const location = useLocation();
  const isSupport = location?.pathname === "/support";

  const [forms, setForms] = useState({
    name: "",
    email: "",
    subject: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");

  const [validator] = useState(
    new SimpleReactValidator({
      className: "gpct-error",
      messages: {
        required: "This field is required.",
        email: "Please enter a valid email.",
        alpha_space: "Letters and spaces only.",
        numeric: "Numbers only.",
      },
    })
  );

  const changeHandler = (e) => {
    setForms({ ...forms, [e.target.name]: e.target.value });
    if (validator.allValid()) validator.hideMessages();
    else validator.showMessages();
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!validator.allValid()) {
      validator.showMessages();
      return;
    }
    setIsSubmitting(true);
    setSubmitResult(null);
    setSubmitMessage("");
    toast.dismiss();
    try {
      const { data } = await axios.post("contact-form", forms);
      setSubmitResult("success");
      setSubmitMessage(data?.message || "Thank you — your message has been sent.");
      toast.success(data?.message || "Message sent successfully.");
      setForms({ name: "", email: "", subject: "", phone: "", message: "" });
      validator.hideMessages();
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      const friendly =
        error?.response?.status === 429
          ? apiMessage || "You can only send one message every 24 hours."
          : apiMessage || "Something went wrong. Please try again later.";
      setSubmitResult("error");
      setSubmitMessage(friendly);
      toast.error(friendly);
    } finally {
      setIsSubmitting(false);
    }
  };

  const heroEyebrow = isSupport ? "WE'RE HERE TO HELP" : "GET IN TOUCH";
  const heroTitle = isSupport ? "Need a hand with " : "We'd love to hear from ";
  const heroAccent = isSupport ? "your order?" : "you";
  const heroSub = isSupport
    ? "Order trouble, refund question or technical issue — message us on WhatsApp or send an email."
    : "Have a question, partnership idea or feedback? Drop us a message and we'll get back to you.";

  return (
    <Fragment>
      <Navbar />

      <section className="gpct-section">
        <div className="gpct-bg-orb gpct-bg-orb-tl" />
        <div className="gpct-bg-orb gpct-bg-orb-br" />

        <div className="gpct-inner">
          <div className="gpct-head">
            <div className="gpct-eyebrow">
              <span className="gpct-eyebrow-dot" />
              {heroEyebrow}
            </div>
            <h1 className="gpct-title">
              {heroTitle}
              <span className="gpct-title-accent">{heroAccent}</span>
            </h1>
            <p className="gpct-sub">{heroSub}</p>
          </div>

          <div className="gpct-info-grid">
            <div className="gpct-info-card">
              <div className="gpct-info-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </div>
              <h4>Visit Us</h4>
              <p>
                Ventureworks Dynamics LLC<br />
                Sharjah Media City (Shams)<br />
                Sharjah, United Arab Emirates
              </p>
            </div>

            <a className="gpct-info-card" href="mailto:support@globalgiftpass.io">
              <div className="gpct-info-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5z" />
                </svg>
              </div>
              <h4>Email Us</h4>
              <p>support@globalgiftpass.io</p>
              <span className="gpct-info-arrow">Send email →</span>
            </a>

            <a className="gpct-info-card" href="tel:+971545740174">
              <div className="gpct-info-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
                </svg>
              </div>
              <h4>Call Us</h4>
              <p>+971 54 574 0174</p>
              <span className="gpct-info-arrow">Call now →</span>
            </a>

            <a
              className="gpct-info-card gpct-info-wa"
              href="https://wa.me/971545740174?text=Hello%20GlobalGiftPass%20support%2C%20I%20need%20help."
              target="_blank"
              rel="noreferrer"
            >
              <div className="gpct-info-icon gpct-icon-wa">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.85 11.85 0 005.71 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.45z" />
                </svg>
              </div>
              <h4>WhatsApp</h4>
              <p>Fastest way to reach us</p>
              <span className="gpct-info-arrow gpct-info-arrow-wa">Chat now →</span>
            </a>
          </div>

          <div className="gpct-form-wrap">
            <div className="gpct-form-side">
              <h2 className="gpct-form-side-title">
                Reach us<br />
                <span>your way</span>
              </h2>
              <p>
                Send us a message via WhatsApp, drop an email, or use the form —
                whichever you prefer. Include your order ID and any screenshots
                so we can resolve it faster.
              </p>
              <ul className="gpct-form-side-list">
                <li>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                  WhatsApp &amp; email channels
                </li>
                <li>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                  Order issues prioritised
                </li>
                <li>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                  Replacement codes for any defective gift card
                </li>
              </ul>
            </div>

            <form className="gpct-form" onSubmit={submitHandler}>
              <h3>Send us a message</h3>
              <p className="gpct-form-sub">We'll never share your details.</p>

              <div className="gpct-row">
                <div className="gpct-field">
                  <label>Your Name</label>
                  <input
                    value={forms.name}
                    type="text"
                    name="name"
                    onBlur={changeHandler}
                    onChange={changeHandler}
                    placeholder="Full name"
                    disabled={isSubmitting}
                  />
                  {validator.message("name", forms.name, "required|alpha_space")}
                </div>
                <div className="gpct-field">
                  <label>Your Email</label>
                  <input
                    value={forms.email}
                    type="email"
                    name="email"
                    onBlur={changeHandler}
                    onChange={changeHandler}
                    placeholder="you@example.com"
                    disabled={isSubmitting}
                  />
                  {validator.message("email", forms.email, "required|email")}
                </div>
              </div>

              <div className="gpct-row">
                <div className="gpct-field">
                  <label>Phone</label>
                  <input
                    value={forms.phone}
                    type="tel"
                    name="phone"
                    onBlur={changeHandler}
                    onChange={changeHandler}
                    placeholder="+971 ..."
                    disabled={isSubmitting}
                  />
                  {validator.message("phone", forms.phone, "required|numeric")}
                </div>
                <div className="gpct-field">
                  <label>Subject</label>
                  <input
                    value={forms.subject}
                    type="text"
                    name="subject"
                    onBlur={changeHandler}
                    onChange={changeHandler}
                    placeholder="Briefly, what's this about?"
                    disabled={isSubmitting}
                  />
                  {validator.message("subject", forms.subject, "required")}
                </div>
              </div>

              <div className="gpct-field">
                <label>Message</label>
                <textarea
                  rows={5}
                  onBlur={changeHandler}
                  onChange={changeHandler}
                  value={forms.message}
                  name="message"
                  placeholder="Tell us how we can help..."
                  disabled={isSubmitting}
                />
                {validator.message("message", forms.message, "required")}
              </div>

              {submitMessage && (
                <div className={`gpct-alert gpct-alert-${submitResult}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    {submitResult === "success" ? (
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    ) : (
                      <path d="M12 2L1 21h22L12 2zm-1 8h2v6h-2v-6zm0 8h2v2h-2v-2z" />
                    )}
                  </svg>
                  {submitMessage}
                </div>
              )}

              <button type="submit" className="gpct-submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="gpct-spinner" />
                ) : (
                  <>
                    Send Message
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <style>{`
          .gpct-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
            min-height: 100vh;
          }
          .gpct-bg-orb {
            position: absolute;
            width: 460px;
            height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gpct-bg-orb-tl { background: rgba(212,168,87,0.18); top: -180px; left: -120px; }
          .gpct-bg-orb-br { background: rgba(212,168,87,0.12); bottom: -200px; right: -120px; }

          .gpct-inner { position: relative; max-width: 1200px; margin: 0 auto; z-index: 1; }

          .gpct-head { text-align: center; margin-bottom: 38px; }
          .gpct-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gpct-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: #d4a857; box-shadow: 0 0 10px #d4a857; animation: gpct-pulse 1.6s ease-in-out infinite; }
          @keyframes gpct-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
          .gpct-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 38px; font-weight: 700; color: #fff; margin: 0 0 10px; line-height: 1.2;
          }
          .gpct-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gpct-sub { font-size: 14.5px; color: #9aa0a8; max-width: 600px; margin: 0 auto; line-height: 1.65; }

          /* INFO GRID */
          .gpct-info-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 14px;
            margin-bottom: 32px;
          }
          .gpct-info-card {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 16px;
            padding: 22px 20px;
            text-decoration: none;
            color: inherit;
            transition: border-color .25s ease, transform .2s ease, box-shadow .25s ease;
            display: block;
          }
          .gpct-info-card:hover {
            border-color: rgba(212,168,87,0.5);
            transform: translateY(-3px);
            box-shadow: 0 12px 28px rgba(212,168,87,0.12);
            text-decoration: none;
            color: inherit;
          }
          .gpct-info-icon {
            width: 42px; height: 42px;
            background: rgba(212,168,87,0.12);
            border: 1px solid rgba(212,168,87,0.28);
            color: #d4a857;
            border-radius: 12px;
            display: flex; align-items: center; justify-content: center;
            margin-bottom: 14px;
          }
          .gpct-icon-wa { background: linear-gradient(135deg, #25d366, #128c7e); border-color: transparent; color: #fff; }
          .gpct-info-card h4 {
            font-family: "Playfair Display", serif;
            font-size: 16px; color: #fff; margin: 0 0 6px; font-weight: 700;
          }
          .gpct-info-card p { font-size: 13px; color: #9aa0a8; margin: 0 0 8px; line-height: 1.55; }
          .gpct-info-arrow {
            display: inline-block;
            font-size: 12px;
            color: #d4a857;
            font-weight: 700;
            letter-spacing: 0.4px;
            text-transform: uppercase;
            transition: gap .2s ease;
          }
          .gpct-info-arrow-wa { color: #25d366; }
          .gpct-info-card:hover .gpct-info-arrow { color: #f1cf85; }
          .gpct-info-card:hover .gpct-info-arrow-wa { color: #34e376; }

          /* FORM WRAP */
          .gpct-form-wrap {
            display: grid;
            grid-template-columns: 0.85fr 1fr;
            gap: 20px;
            align-items: stretch;
          }
          .gpct-form-side {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 18px;
            padding: 32px 28px;
          }
          .gpct-form-side-title {
            font-family: "Playfair Display", serif;
            font-size: 28px;
            color: #fff;
            font-weight: 700;
            margin: 0 0 14px;
            line-height: 1.2;
          }
          .gpct-form-side-title span {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gpct-form-side > p {
            font-size: 14px; color: #9aa0a8; line-height: 1.65; margin: 0 0 22px;
          }
          .gpct-form-side-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
          .gpct-form-side-list li {
            display: flex; align-items: flex-start; gap: 10px;
            font-size: 13.5px; color: #c5c8cf; line-height: 1.45;
          }
          .gpct-form-side-list svg { color: #d4a857; flex-shrink: 0; margin-top: 3px; }

          .gpct-form {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 18px;
            padding: 32px 28px;
          }
          .gpct-form h3 {
            font-family: "Playfair Display", serif;
            font-size: 22px; color: #fff; font-weight: 700; margin: 0 0 4px;
          }
          .gpct-form-sub { font-size: 13px; color: #9aa0a8; margin: 0 0 22px; }

          .gpct-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
          .gpct-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
          .gpct-field label {
            font-size: 11px;
            color: #9aa0a8;
            font-weight: 700;
            letter-spacing: 1.2px;
            text-transform: uppercase;
          }
          .gpct-field input,
          .gpct-field textarea {
            background: rgba(0,0,0,0.35);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 10px;
            color: #fff;
            font-size: 14px;
            padding: 12px 14px;
            outline: none;
            font-family: inherit;
            transition: border-color .2s ease, background .2s ease;
          }
          .gpct-field input::placeholder, .gpct-field textarea::placeholder { color: #6e7480; }
          .gpct-field input:focus, .gpct-field textarea:focus {
            border-color: rgba(212,168,87,0.6);
            background: rgba(0,0,0,0.5);
          }
          .gpct-field input:disabled, .gpct-field textarea:disabled { opacity: 0.6; cursor: not-allowed; }
          .gpct-field textarea { resize: vertical; min-height: 110px; }

          .gpct-error {
            display: block;
            color: #ff7a7a;
            font-size: 12px;
            font-weight: 600;
            margin-top: 4px;
          }

          .gpct-alert {
            display: flex; align-items: center; gap: 8px;
            padding: 10px 14px;
            font-size: 13px;
            font-weight: 600;
            border-radius: 10px;
            margin-bottom: 14px;
          }
          .gpct-alert-success {
            background: rgba(74, 222, 128, 0.1);
            border: 1px solid rgba(74, 222, 128, 0.3);
            color: #4ade80;
          }
          .gpct-alert-error {
            background: rgba(255, 90, 90, 0.1);
            border: 1px solid rgba(255, 90, 90, 0.3);
            color: #ff7a7a;
          }

          .gpct-submit {
            width: 100%;
            display: inline-flex; align-items: center; justify-content: center; gap: 10px;
            padding: 14px;
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
            border: none;
            border-radius: 12px;
            font-size: 13px;
            font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            cursor: pointer;
            font-family: inherit;
            min-height: 50px;
            transition: transform .2s ease, box-shadow .25s ease, gap .2s ease;
          }
          .gpct-submit:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            gap: 12px;
          }
          .gpct-submit:disabled { opacity: 0.7; cursor: not-allowed; }
          .gpct-spinner {
            width: 18px; height: 18px;
            border: 2px solid rgba(11,11,12,0.3);
            border-top-color: #0b0b0c;
            border-radius: 50%;
            animation: gpct-spin .7s linear infinite;
          }
          @keyframes gpct-spin { to { transform: rotate(360deg); } }

          @media (max-width: 991px) {
            .gpct-info-grid { grid-template-columns: repeat(2, 1fr); }
            .gpct-form-wrap { grid-template-columns: 1fr; }
            .gpct-title { font-size: 30px; }
          }
          @media (max-width: 575px) {
            .gpct-section { padding: 40px 14px 70px; }
            .gpct-info-grid { grid-template-columns: 1fr; }
            .gpct-form, .gpct-form-side { padding: 24px 20px; }
            .gpct-row { grid-template-columns: 1fr; gap: 0; }
            .gpct-title { font-size: 24px; }
            .gpct-form-side-title { font-size: 22px; }
          }
        `}</style>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default ContactForm;
